// Copyright 2026, Common Good Learning Tools LLC
import { mapState, mapGetters } from 'vuex'

export default {
	data() { return {
		job_status: null,
		resource_universe: null,
		comp_universe: null,
		resource_aggregation_type: '',

		// note that the matrix and sub_matrix are indexed first by standard (comp_id), then by chapter (resource_id)
		matrix: null,
		sub_matrix: null,
		rfr: null,
		cfr: null,

		comp_framework_id:'',
		resource_framework_id:'',

		resource_branch_showing: [],
	}},
	computed: {
		...mapState(['user_info']),
		...mapGetters([]),

		resource_cfitems() {
			if (!this.initialized) return {}
			let o = {}
			for (let resource_id of this.resource_ids) {
				o[resource_id] = this.rfr.cfo.cfitems[resource_id]
			}
			return o
		},

		comp_cfitems() {
			if (!this.initialized) return {}
			let o = {}
			for (let comp_id of this.comp_ids) {
				o[comp_id] = this.cfr.cfo.cfitems[comp_id]
			}
			return o
		},

		resource_set_title() {
			if (!this.rfr) return ''
			// TODO: make this configurable, or get it from the parent of the parent matrix??...
			return this.rfr.json.CFDocument.title
			// let cfi = this.rfr.cfo.cfitems[this.resource_branch]
			// return cfi.fullStatement
		},

		comp_framework_href() {
			// TODO: get this from the parent of the first competency??
			// return `https://rosetta.commongoodlt.com/${this.comp_framework_id}/${this.comp_branches[0]}`
			return `https://rosetta.commongoodlt.com/${this.comp_framework_id}`
		},

		comp_framework_title() {
			if (!this.cfr) return ''
			let agency = this.cfr.json.CFDocument.creator
			agency = agency.replace(/Department of Education/, '')
			agency = agency.replace(/Department of Public Instruction/, '')
			agency = agency.replace(/Department of Public Instruction/, '')
			agency = agency.trim()

			let subject = U.framework_subject_guess(this.rfr.json.CFDocument)

			return `${agency} — ${subject.label}`
		},

		////////////////////////////////////
		resource_branch_items() {
			let arr = []
			if (!this.resource_universe) return arr

			// use the item type of the first resource branch; assume they are all the same if this computed is being used for something
			if (this.resource_universe?.[0].CASEGroupIdentifiers) {
				for (let branch_id of this.resource_universe[0].CASEGroupIdentifiers) {
					let item = this.rfr.cfo.cfitems[branch_id]
					if (!item) {
						console.warn('couldn’t find resource_branch for ' + branch_id)
						continue
					}
					arr.push(item)
				}
			}
			return arr
		},

		resource_branch_item_type() {
			if (!this.resource_universe) return ''
			if (this.resource_branch_items.length == 0) return ''
			return U.item_type_string(this.resource_branch_items[0]) || 'Resource Set'
		},

		resource_branch_item_type_plural() {
			if (!this.resource_universe) return ''
			return this.resource_branch_item_type + 's'
		},

		resource_branch_colors() {
			if (this.show_print_view) return ''
			if (this.resource_branch_items.length <= 5) {
				return [
					'deep-purple darken-4',
					'teal darken-4',
					'lime darken-4',
					'orange darken-4',
					'brown darken-4',
				]

			} else {
				let arr = []
				for (let i = 0; i < this.resource_branch_items.length; ++i) {
					arr.push('blue-grey darken-3')
				}
				return arr
			}
		},

		n_resource_branches_showing() {
			let n = 0
			for (let x of this.resource_branch_showing) {
				if (x) n += 1
			}
			return n
		},

		// Currently used in MatrixView TODO: get these from the elements, and/or have them specified in the job
		comp_item_type() { return 'Standard Element' },
		comp_label() { return 'Standard' },
		comp_label_plural() { return 'Standards' },

		resource_item_type() { return 'Chapter' },
		resource_label() { return 'Chapter' },
		resource_label_plural() { return 'Chapters' },

		sub_resource_item_type() { return 'Section' },
		sub_resource_label() { return 'Section' },
		sub_resource_label_plural() { return 'Sections' },
		
		description_label() { return 'Summary' },
		description_label_plural() { return 'Summaries' },

		///////////////////////////////////
		// "meta-settings" for reports

		report_banner_bgd_color() {
			return `#E3F2FD`
		},

		report_logo() {
			// TODO: store this as part of the job
			return `/src/logos/bju_press_logo.png`
			// return `/images/cglt-globe-with-stars.svg`
		},
		
		report_logo_style() {
			return ``
			// return `left:-36px; top:-36px; height:100px`
		},
		
		// note that report_title is in the individual report components

		report_intro() {
			// TODO: store this as part of the job
			return `This document compares BJU Press courses with educational standards. Correlations were generated using AI tools developed by Common Good Learning Tools, a non-profit organization, through a carefully developed, multi-step review process. Only correlations meeting a high degree of confidence are included, and citations are provided for your reference. This report is intended to support the evaluation and use of BJU Press textbooks — recognizing that achievement of educational outcomes is ultimately accomplished by the teacher in the classroom, as part of their overall curricular program.`
		},
	},

	methods: {
		async load_data(process_missing = false) {
			let result

			// "cache" results in vapp (makes loading faster during development)
			if (true && vapp.report_cram_job_id == this.cram_job_id) {
				result = vapp.cram_report_result

			} else {
				// get the matrix
				let payload = {
					user_id: this.user_info.user_id,
					action: 'get_matrix',
					cram_job_id: this.cram_job_id,
				}

				U.loading_start()
				result = await U.ajax_promise('align/scram', payload)
				U.loading_stop()
				console.log(result)

				if (result.status != 'ok') {
					this.$alert('Error compiling alignment matrix');
					return
				}

				// cache in vapp
				vapp.report_cram_job_id = this.cram_job_id
				vapp.cram_report_result = sc(result)
			}

			// parse job_status and universes
			if (!empty(result.job_status)) {
				this.job_status = result.job_status
				this.resource_universe = JSON.parse(this.job_status.universe_params.resourceUniverse)
				this.comp_universe = JSON.parse(this.job_status.universe_params.competencyUniverse)
				this.resource_aggregation_type = this.job_status.resourceAggregationItemType
			}

			// if this job includes parent/child matrices, store like this
			if (!empty(result.parent_matrix)) {
				this.matrix = result.parent_matrix
				this.sub_matrix = result.matrix
			} else {
				this.matrix = result.matrix
			}

			// get all resource and competency framework_ids from matrix
			// TODO: for now we're just supporting one each...
			for (let comp_id in this.matrix) {
				for (let resource_id in this.matrix[comp_id]) {
					this.resource_framework_id = this.matrix[comp_id][resource_id].resource_framework_id
					this.comp_framework_id = this.matrix[comp_id][resource_id].competency_framework_id
				}
			}

			// get the resource and comp framework_record
			this.rfr = await U.load_framework_and_cfo(this.resource_framework_id)
			this.cfr = await U.load_framework_and_cfo(this.comp_framework_id)

			// set up resource_branch_showing
			let arr = []
			for (let i = 0; i < this.resource_branch_items.length; ++i) {
				arr.push(true)
			}
			this.resource_branch_showing = arr
		},

		get_ancestors(comp_id) {
			let cfi = this.cfr.cfo.cfitems[comp_id]
			if (empty(cfi)) return []	// shouldn't happen
			let node = cfi.tree_nodes[0]?.parent_node
			let arr = []

			// trace up until we hit the document
			while (node && !node.cfitem?.title) {
				arr.unshift(node.cfitem)
				node = node.parent_node
			}

			return arr
		},

		cfitem_title(cfi) {
			let s = cfi.fullStatement
			if (!empty(cfi.humanCodingScheme)) s = `**${cfi.humanCodingScheme}** ${s}`
			return U.marked_latex(s)
		},
	}
}
